/**
 * @krisspy-file
 * @type page
 * @name "LoginError"
 * @title "Login Error"
 * @description "Display authentication error message"
 * @routes ["/login/error"]
 * @pathExample "/login/error"
 * @layout "AuthLayout"
 * @flowName "authentication"
 */

import { AlertCircle, Mail, Lock, LogIn, ArrowLeft, HelpCircle } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function LoginError() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Get error type from URL params (e.g., ?error=invalid_credentials or ?error=account_locked)
  const errorType = searchParams.get('error') || 'invalid_credentials';
  const attemptCount = searchParams.get('attempts') || '1';

  // Error messages based on type
  const errorMessages: Record<string, { title: string; message: string; showLocked?: boolean }> = {
    invalid_credentials: {
      title: t('login.invalidCredentials'),
      message: t('login.invalidCredentialsMessage'),
    },
    account_locked: {
      title: t('login.accountLocked'),
      message: t('login.accountLockedMessage'),
      showLocked: true,
    },
    session_expired: {
      title: t('login.sessionExpired'),
      message: t('login.sessionExpiredMessage'),
    },
    network_error: {
      title: t('login.connectionError'),
      message: t('login.connectionErrorMessage'),
    },
  };

  const currentError = errorMessages[errorType] || errorMessages.invalid_credentials;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // Simulate authentication retry (prototype - no backend)
    setTimeout(() => {
      setIsLoading(false);
      navigate('/dashboard');
    }, 1000);
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const handleForgotPassword = () => {
    navigate('/reset-password');
  };

  return (
    <div className="space-y-xl">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-primary mb-sm">{t('login.welcomeBack')}</h2>
        <p className="text-secondary text-sm">{t('login.signInDescription')}</p>
      </div>

      {/* Error Alert */}
      <div className={`rounded-lg border p-md ${
        currentError.showLocked
          ? 'bg-red-50 border-red-200'
          : 'bg-orange-50 border-orange-200'
      }`}>
        <div className="flex gap-md">
          <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
            currentError.showLocked ? 'text-red-600' : 'text-orange-600'
          }`} />
          <div className="flex-1">
            <h3 className={`font-semibold mb-xs ${
              currentError.showLocked ? 'text-red-900' : 'text-orange-900'
            }`}>
              {currentError.title}
            </h3>
            <p className={`text-sm ${
              currentError.showLocked ? 'text-red-700' : 'text-orange-700'
            }`}>
              {currentError.message}
            </p>
            {errorType === 'invalid_credentials' && parseInt(attemptCount) > 2 && (
              <p className="text-sm text-orange-600 mt-sm font-medium">
                {t('login.failedAttemptsWarning', { count: parseInt(attemptCount) })}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Login Form - Only show if not locked */}
      {!currentError.showLocked && (
        <form onSubmit={handleLogin} className="space-y-lg">
          {/* Email Input */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-sm text-primary">
              {t('login.emailAddress')}
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('login.emailPlaceholder')}
                className="input pl-11"
                required
              />
            </div>
          </div>

          {/* Password Input */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-sm text-primary">
              {t('login.password')}
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary" />
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('login.passwordPlaceholder')}
                className="input pl-11"
                required
              />
            </div>
          </div>

          {/* Remember Me & Forgot Password */}
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-sm cursor-pointer">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-primary accent-blue cursor-pointer"
              />
              <span className="text-sm text-primary">{t('login.rememberMe')}</span>
            </label>
            <button
              type="button"
              onClick={handleForgotPassword}
              className="text-sm text-blue hover:text-blue-hover transition-colors"
            >
              {t('login.forgotPassword')}
            </button>
          </div>

          {/* Login Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary w-full"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('login.signingIn')}
              </>
            ) : (
              <>
                <LogIn className="w-4 h-4" />
                {t('login.tryAgain')}
              </>
            )}
          </button>
        </form>
      )}

      {/* Account Locked Actions */}
      {currentError.showLocked && (
        <div className="space-y-md">
          <button
            type="button"
            onClick={handleForgotPassword}
            className="btn btn-primary w-full"
          >
            {t('login.resetPassword')}
          </button>
          <button
            type="button"
            onClick={handleBackToLogin}
            className="btn btn-secondary w-full"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('login.backToLogin')}
          </button>
        </div>
      )}

      {/* Support Link */}
      <div className="pt-lg border-t border-border">
        <button
          type="button"
          onClick={() => window.open('mailto:support@rental.com', '_blank')}
          className="flex items-center justify-center gap-sm text-sm text-secondary hover:text-primary transition-colors w-full"
        >
          <HelpCircle className="w-4 h-4" />
          {t('login.needHelpContactSupport')}
        </button>
      </div>
    </div>
  );
}
